import {
  Banner, Button, DataTable, InlineGrid, InlineStack, Layout, LegacyCard,
  Page, Select, Spinner, Text, TextField,
} from "@shopify/polaris";
import { useCallback, useEffect, useMemo, useState } from "react";
import { admin_store_performance } from "../api";

const todayUtc = () => new Date().toISOString().slice(0, 10);
const monthStartUtc = () => `${todayUtc().slice(0, 7)}-01`;
const orderTypes = [
  { label: "All orders", value: "all" },
  { label: "Online orders", value: "online" },
  { label: "COD orders", value: "cod" },
];

function money(amounts = {}) {
  const entries = Object.entries(amounts);
  if (!entries.length) return "—";
  return entries.map(([currency, amount]) => {
    const value = Number(amount);
    try {
      return new Intl.NumberFormat(undefined, { style: "currency", currency, maximumFractionDigits: 4 }).format(value);
    } catch {
      return `${amount} ${currency}`;
    }
  }).join(" · ");
}

export default function StorePerformance() {
  const [startDate, setStartDate] = useState(monthStartUtc);
  const [endDate, setEndDate] = useState(todayUtc);
  const [orderType, setOrderType] = useState("all");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await admin_store_performance({ start_date: startDate, end_date: endDate, order_type: orderType });
      if (response.status === 200) setReport(response.data);
      else setError(response.msg || "Unable to load store performance.");
    } catch (requestError) {
      setError(requestError?.response?.data?.msg || "Unable to load store performance.");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, orderType]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => (report?.stores || []).map((store) => [
    <div><Text as="span" variant="bodyMd" fontWeight="semibold">{store.shop_name || `Shop ${store.shop_id}`}</Text>{store.app_name && <Text as="p" tone="subdued">{store.app_name}</Text>}</div>,
    store.is_active ? "Active" : "Inactive",
    store.order_count,
    store.confirmed_order_count,
    money(store.confirmed_amounts),
  ]), [report]);

  return (
    <Page title="Store performance" subtitle="Order performance by source Shopify store">
      <Layout>
        {error && <Layout.Section><Banner tone="critical" title="Unable to load report">{error}</Banner></Layout.Section>}
        <Layout.Section>
          <LegacyCard sectioned>
            <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="400">
              <TextField label="Start date (UTC)" type="date" value={startDate} onChange={setStartDate} autoComplete="off" />
              <TextField label="End date (UTC)" type="date" value={endDate} onChange={setEndDate} autoComplete="off" />
              <Select label="Order type" options={orderTypes} value={orderType} onChange={setOrderType} />
              <InlineStack align="start" blockAlign="end"><Button variant="primary" onClick={load} loading={loading}>Apply</Button></InlineStack>
            </InlineGrid>
          </LegacyCard>
        </Layout.Section>
        <Layout.Section>
          <Banner>
            Counts include orders created in the selected UTC dates. Confirmed online orders have status 1; confirmed COD orders have status 3 (accepted). Amounts are grouped by currency and represent order value, not signed delivery or repayment.
          </Banner>
        </Layout.Section>
        <Layout.Section>
          {loading && !report ? <Spinner accessibilityLabel="Loading store performance" /> : <InlineGrid columns={{ xs: 1, sm: 3 }} gap="400">
            <LegacyCard title="Orders" sectioned><Text as="p" variant="headingLg">{report?.totals?.order_count ?? 0}</Text></LegacyCard>
            <LegacyCard title="Confirmed orders" sectioned><Text as="p" variant="headingLg">{report?.totals?.confirmed_order_count ?? 0}</Text></LegacyCard>
            <LegacyCard title="Confirmed order value" sectioned><Text as="p" variant="headingMd">{money(report?.totals?.confirmed_amounts)}</Text></LegacyCard>
          </InlineGrid>}
        </Layout.Section>
        <Layout.Section>
          <LegacyCard title="Performance by store">
            <DataTable
              columnContentTypes={["text", "text", "numeric", "numeric", "text"]}
              headings={["Shop", "Status", "Orders", "Confirmed", "Confirmed order value"]}
              rows={rows}
              loading={loading}
              emptyState={<Text as="p" tone="subdued">No stores or orders match this period.</Text>}
            />
          </LegacyCard>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
